import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  Download,
  LogOut,
  Play,
  RefreshCw,
  Square,
  Trash2,
  User,
  FileText,
  HardDrive,
  Clock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../services/api';
import { getVersionString } from '../version';
import ThemeToggle from '../components/ThemeToggle';

interface TrafficCaptureStatus {
  enabled: boolean;
  fileSize: number;
  requestCount: number;
  userIpFilter: string | null;
  sessionId: string | null;
  startedBy: string | null;
  startedAt: string | null;
}

const formatBytes = (bytes?: number | null) => {
  if (!bytes) return '0 B';
  if (bytes >= 1024 ** 3) {
    return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
  }
  if (bytes >= 1024 ** 2) {
    return `${(bytes / 1024 ** 2).toFixed(2)} MB`;
  }
  if (bytes >= 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  }
  return `${bytes} B`;
};

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString() : '0';

export default function TrafficCapturePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [status, setStatus] = useState<TrafficCaptureStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [userIpFilter, setUserIpFilter] = useState('');
  const [showUserIpInput, setShowUserIpInput] = useState(true); // Default: enabled for privacy
  const [lastUpdated, setLastUpdated] = useState<string>('');

  const loadStatus = useCallback(async () => {
    try {
      const authCheck = await authAPI.checkAdminAuth();
      if (!authCheck.authenticated) {
        navigate('/admin');
        return;
      }

      const response = await fetch('/api/admin/traffic-capture/status', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch status');
      }

      const data = await response.json();
      setStatus(data);
      setLastUpdated(new Date().toLocaleTimeString('ja-JP', { timeZone: 'Asia/Tokyo', hour12: false }));
      setError(null);
    } catch (err: any) {
      console.error('Load status error', err);
      setError(err.message || 'Failed to load status');
    }
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await loadStatus();
      } catch (err: any) {
        console.error('Initial load error', err);
        setError(err.message || 'データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadStatus]);

  // Auto-refresh every 5 seconds when capture is active
  useEffect(() => {
    if (!status?.enabled) return;

    const interval = setInterval(() => {
      loadStatus().catch((err) => {
        console.error('Auto refresh error', err);
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [status?.enabled, loadStatus]);

  const handleStartCapture = async () => {
    try {
      setActionLoading(true);
      setError(null);

      const body = showUserIpInput && userIpFilter ? { userIp: userIpFilter } : {};

      const response = await fetch('/api/admin/traffic-capture/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to start capture');
      }

      const data = await response.json();
      alert(data.message || 'Traffic capture started');
      await loadStatus();
      setShowUserIpInput(false);
      setUserIpFilter('');
    } catch (err: any) {
      console.error('Start capture error', err);
      setError(err.message || 'Failed to start capture');
    } finally {
      setActionLoading(false);
    }
  };

  const handleStopCapture = async () => {
    try {
      setActionLoading(true);
      setError(null);

      const response = await fetch('/api/admin/traffic-capture/stop', {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to stop capture');
      }

      const data = await response.json();
      alert(data.message || 'Traffic capture stopped');
      await loadStatus();
    } catch (err: any) {
      console.error('Stop capture error', err);
      setError(err.message || 'Failed to stop capture');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      setActionLoading(true);
      setError(null);

      const response = await fetch('/api/admin/traffic-capture/download', {
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        if (response.status === 429) {
          throw new Error(data.message || 'Rate limit exceeded. Please wait 1 minute.');
        }
        throw new Error(data.error || 'Failed to download');
      }

      // Create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `traffic_capture_${new Date().toISOString().split('T')[0]}.jsonl`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('Download error', err);
      setError(err.message || 'Failed to download');
    } finally {
      setActionLoading(false);
    }
  };

  const handleClear = async () => {
    if (!confirm('Are you sure you want to clear all captured traffic? This cannot be undone.')) {
      return;
    }

    try {
      setActionLoading(true);
      setError(null);

      const response = await fetch('/api/admin/traffic-capture/clear', {
        method: 'POST',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to clear');
      }

      const data = await response.json();
      alert(data.message || 'Traffic log cleared');
      await loadStatus();
    } catch (err: any) {
      console.error('Clear error', err);
      setError(err.message || 'Failed to clear');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManualRefresh = async () => {
    try {
      setActionLoading(true);
      await loadStatus();
    } catch (err: any) {
      console.error('Manual refresh error', err);
      setError(err.message || 'Failed to refresh');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authAPI.adminLogout();
    } finally {
      sessionStorage.clear();
      localStorage.clear();
      window.location.href = '/admin';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-kido-50 dark:bg-[#1a1612] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="sticky top-6 z-50 bg-white dark:bg-warm-gray-800 rounded-lg shadow-subtle p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Activity className="w-8 h-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">{t('trafficCapture.title')}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Capture all proxy traffic for monitoring and analysis
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500">{getVersionString()}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="px-4 py-2 bg-gray-100 dark:bg-warm-gray-700 hover:bg-gray-200 dark:hover:bg-warm-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
              >
                {t('nav.dashboard')}
              </button>
              <ThemeToggle />
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                {t('nav.logout')}
              </button>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-red-800 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Status Card */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-subtle p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
              <Activity className="w-6 h-6" />
              Capture Status
            </h2>
            <button
              onClick={handleManualRefresh}
              disabled={actionLoading}
              className="flex items-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${actionLoading ? 'animate-spin' : ''}`} />
              {t('common.refresh')}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Status */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-5 h-5 text-blue-700" />
                <p className="text-sm font-medium text-blue-700">Status</p>
              </div>
              <p className="text-2xl font-bold text-blue-900">
                {status?.enabled ? 'Running' : 'Stopped'}
              </p>
            </div>

            {/* File Size */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <HardDrive className="w-5 h-5 text-purple-700" />
                <p className="text-sm font-medium text-purple-700">File Size</p>
              </div>
              <p className="text-2xl font-bold text-purple-900">
                {formatBytes(status?.fileSize)}
              </p>
            </div>

            {/* Request Count */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-5 h-5 text-green-700" />
                <p className="text-sm font-medium text-green-700">Requests</p>
              </div>
              <p className="text-2xl font-bold text-green-900">
                {formatNumber(status?.requestCount)}
              </p>
            </div>

            {/* User Filter */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <User className="w-5 h-5 text-orange-700" />
                <p className="text-sm font-medium text-orange-700">User Filter</p>
              </div>
              <p className="text-lg font-bold text-orange-900">
                {status?.userIpFilter || 'All Users'}
              </p>
            </div>
          </div>

          {/* Additional Info */}
          {status?.enabled && (
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-warm-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>
                    Started: {status.startedAt ? new Date(status.startedAt).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', hour12: false }) : '-'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>Started by: {status.startedBy || '-'}</span>
                </div>
              </div>
            </div>
          )}

          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-right">
            Last updated: {lastUpdated}
          </div>
        </div>

        {/* Control Panel */}
        <div className="bg-white dark:bg-warm-gray-800 rounded-lg shadow-subtle p-6">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">Control Panel</h2>

          {!status?.enabled ? (
            <div className="space-y-4">
              {/* User IP Filter Option */}
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3 mb-2">
                  <input
                    type="checkbox"
                    id="userIpFilterToggle"
                    checked={showUserIpInput}
                    onChange={(e) => setShowUserIpInput(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <label htmlFor="userIpFilterToggle" className="text-sm font-semibold text-blue-900 dark:text-blue-300">
                    Capture only specific user (recommended for privacy)
                  </label>
                </div>
                <p className="text-xs text-blue-700 ml-7">
                  ✅ Recommended: Always specify user IP to minimize sensitive data exposure
                </p>
              </div>

              {showUserIpInput && (
                <input
                  type="text"
                  value={userIpFilter}
                  onChange={(e) => setUserIpFilter(e.target.value)}
                  placeholder="Enter VPN IP (e.g., 10.77.1.23)"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-warm-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-warm-gray-700 dark:text-white"
                />
              )}

              {/* Start Button */}
              <button
                onClick={handleStartCapture}
                disabled={actionLoading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Play className="w-5 h-5" />
                {t('trafficCapture.startCapture')}
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  Traffic capture is running. Auto-stop at 100MB to prevent disk fill.
                </p>
              </div>

              {/* Stop Button */}
              <button
                onClick={handleStopCapture}
                disabled={actionLoading}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Square className="w-5 h-5" />
                {t('trafficCapture.stopCapture')}
              </button>
            </div>
          )}

          {/* Download and Clear Buttons */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <button
              onClick={handleDownload}
              disabled={actionLoading || !status?.fileSize}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-5 h-5" />
              {t('common.download')}
            </button>

            <button
              onClick={handleClear}
              disabled={actionLoading || !status?.fileSize}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-5 h-5" />
              Clear Log
            </button>
          </div>

          <div className="mt-4 p-4 bg-gray-50 dark:bg-warm-gray-700 rounded-lg text-sm text-gray-600 dark:text-gray-400">
            <p className="font-semibold mb-2">Notes:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Download is rate limited to 1 request per minute</li>
              <li>Capture will auto-stop at 100MB to prevent disk fill</li>
              <li>All actions are logged in audit table for compliance</li>
              <li>User filter minimizes sensitive data exposure</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
